import {
  BoatCoverage,
  CaravanCoverage,
  InsuranceCoverage,
  MotorcycleCoverage,
} from "Store/interfaces/products/coverage";
import { isDefined, isEmpty, isUndefined } from "Utils/functions";
import {
  CUSTOM_VETERAN_AUTO_TYPE_YEARS_END,
  CUSTOM_VETERAN_AUTO_TYPE_YEARS_START,
  VEHICLE_TYPE,
  getVehicleTypeYears,
} from "./vehicleConstants";

import { Option } from "@storeblocks/radio";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { formatNumber } from "Formatting/index";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  CalculationPrice,
  PriceCalculationEntry,
} from "Store/priceCalculation/priceCalculationInterfaces";
import { filterSoonToBeDeprecatedDeductibles } from "Utils/priceUtils";

export function getDiscount(
  coverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
  deductible?: string,
  defaultDeductible?: string,
): number {
  const price = getPrice(coverages, coverage, deductible, defaultDeductible);

  if (isUndefined(price)) {
    return 0;
  }

  return price.fullYearlyPricePrecise &&
    price.fullYearlyPricePrecise > price.yearlyPricePrecise
    ? price.fullYearlyPricePrecise - price.yearlyPricePrecise
    : 0;
}

const getChosenCoverage = (
  selectedCoverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
): CoveragePriceCalculation | undefined =>
  coverages.find(([coverage]) => coverage === selectedCoverage);

export function getPrice(
  coverages: CoveragePriceCalculation[],
  coverage: InsuranceCoverage,
  deductible: string | undefined,
  defaultDeductible: string | undefined,
): CalculationPrice | undefined {
  const chosenCoverage = getChosenCoverage(coverage, coverages);
  const price = chosenCoverage?.[1].prices.find(
    (productPrice) => productPrice.value === (deductible || defaultDeductible),
  );

  if (!isDefined(price)) {
    return chosenCoverage?.[1].prices[0];
  }
  return price;
}

export function getYearlyTffAmount(
  coverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
): number | undefined {
  const tfaAmount = getTfaAmount(coverage, coverages);

  if (isUndefined(tfaAmount)) {
    return undefined;
  }

  return tfaAmount.yearly;
}

export function getMonthlyTffAmount(
  coverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
): number | undefined {
  const tfaAmount = getTfaAmount(coverage, coverages);

  if (isUndefined(tfaAmount)) {
    return undefined;
  }

  return tfaAmount.monthly;
}

export const getValidVeteranAutoRegistrationYearOptions = (
  modelAndValue: string | undefined,
): Array<{
  label: string;
  value: string;
}> => {
  const currentYear =
    new Date().getFullYear() - CUSTOM_VETERAN_AUTO_TYPE_YEARS_START - 1;
  return Array.from(
    { length: getCustomVeteranAutoTypeYearsEnd(modelAndValue) },
    (_, index) => ({
      label: String(currentYear - index),
      value: String(currentYear - index),
    }),
  );
};

export const getValidRegistrationYearOptions = (
  vehicleType: VEHICLE_TYPE,
): Array<{
  label: string;
  value: string;
}> => {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: getVehicleTypeYears(vehicleType) },
    (_, index) => ({
      label: String(currentYear - index),
      value: String(currentYear - index),
    }),
  );
};

function getTfaAmount(
  coverage: InsuranceCoverage,
  coverages: CoveragePriceCalculation[],
): PriceCalculationEntry["tfaAmount"] | undefined {
  const chosenCoverage = getChosenCoverage(coverage, coverages);
  return chosenCoverage?.[1]?.tfaAmount;
}

export function getDeductibles(
  insuranceName: INSURANCE_NAME_VALUE,
  selected: CaravanCoverage | MotorcycleCoverage | BoatCoverage,
  coverages: CoveragePriceCalculation[],
): Option[] {
  const prices = coverages
    .find(([coverage]) => coverage === selected)?.[1]
    .prices.filter((price) => price.code !== "4" && price.value !== "30000")
    .filter((price) =>
      filterSoonToBeDeprecatedDeductibles(insuranceName, price.code),
    )
    .map((price) => Number(price.value))
    .sort((a, b) => a - b)
    .filter((deductible) => !Number.isNaN(deductible));

  if (!isDefined(prices)) {
    return [];
  }

  return prices.map((price) => ({
    id: `deductible-option-${price}`,
    label: `${formatNumber(String(price), false)} kr`,
    value: String(price),
  }));
}

const getCustomVeteranAutoTypeYearsEnd = (
  modelAndValue: string | undefined,
): number => {
  // Regular expression to search for a 4-digit number
  const numberRegex = /\b\d{4}\b/;
  // Regular expression to search for the word "eldre"
  const olderRegex = /\beldre\b/;

  if (!isDefined(modelAndValue) || isEmpty(modelAndValue)) {
    return CUSTOM_VETERAN_AUTO_TYPE_YEARS_END;
  }

  if (numberRegex.test(modelAndValue)) {
    const yearFoundInModel = numberRegex.exec(modelAndValue)?.[0];
    if (yearFoundInModel === undefined || olderRegex.test(modelAndValue)) {
      return CUSTOM_VETERAN_AUTO_TYPE_YEARS_END;
    }
    return (
      new Date().getFullYear() -
      Number(yearFoundInModel) -
      CUSTOM_VETERAN_AUTO_TYPE_YEARS_START
    );
  }

  return CUSTOM_VETERAN_AUTO_TYPE_YEARS_END;
};
